import React, { useState, useMemo } from 'react'
import {
  Box, Typography, Stack, TextField, MenuItem, Button, Pagination, Chip,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Navebarehero } from '../Components/Navebarehero'
import { blogsData, getUniqueCategories } from '../Data/blogsData'
import vedio from '../Images/burj-khalifa-skyscraper.mp4'

const PAGE_SIZE = 6

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#D9D6CF',
    borderRadius: '12px',
    bgcolor: 'rgba(217,214,207,0.06)',
    '& fieldset': { borderColor: 'rgba(217,214,207,0.25)' },
    '&:hover fieldset': { borderColor: 'rgba(217,214,207,0.45)' },
    '&.Mui-focused fieldset': { borderColor: '#7A2E3A' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(217,214,207,0.65)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#7A2E3A' },
}

// هيرو مصغّر لصفحة كل المقالات - العنوان في منتصف الفيديو، والفيلتر طافي نص فوق ونص تحت حدود الهيرو
const BlogsPageHero = ({ isArabic, filterBar }) => (
  <Box sx={{ position: 'relative', width: '100%', height: { xs: '75vh', md: '85vh' }, bgcolor: '#2B2B2B' }}>
    {/* الفيديو والعنوان جوه container له overflow hidden لوحده */}
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
      >
        <source src={vedio} type="video/mp4" />
      </Box>

      {/* overlay أساسي لتحسين تباين النص */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background: 'linear-gradient(180deg, rgba(43,43,43,0.6) 0%, rgba(122,46,58,0.15) 50%, rgba(43,43,43,0.55) 100%)',
        }}
      />

      {/* ✨ gradient في آخر جزء من الفيديو فقط ليمتزج مع خلفية الصفحة */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '35%',
          background:
            'linear-gradient(180deg, rgba(43,43,43,0) 0%, rgba(43,43,43,0.5) 40%, rgba(43,43,43,0.9) 80%, #2B2B2B 100%)',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />

      {/* العنوان والوصف - في المنتصف الرأسي الحقيقي لمنطقة الهيرو */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 1.5,
          px: 2,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontFamily: isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif",
            fontWeight: isArabic ? 700 : 600,
            fontSize: { xs: '2.3rem', md: '3.8rem' },
            color: '#fff',
          }}
        >
          {isArabic ? 'كل المقالات' : 'All Blogs'}
        </Typography>
        <Typography sx={{ color: 'rgba(217,214,207,0.8)', maxWidth: 480 }}>
          {isArabic
            ? 'استكشف مجموعتنا الكاملة من المقالات والنصائح العقارية في دبي'
            : 'Explore our full collection of real estate insights and tips in Dubai'}
        </Typography>
      </Box>
    </Box>

    {/* شريط الفيلتر - برة الـ overflow:hidden، فبيبان كامل حتى لو طالع برة حدود الهيرو */}
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        transform: 'translateY(50%)',
        zIndex: 5,
        px: { xs: 2, md: 6 },
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1300 }}>{filterBar}</Box>
    </Box>
  </Box>
)

export const AllBlogs = () => {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  const lang = isArabic ? 'ar' : 'en'

  const headingFont = isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif"
  const bodyFont = isArabic ? "'IBM Plex Sans Arabic', sans-serif" : "'Manrope', sans-serif"

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const [page, setPage] = useState(1)

  const categories = useMemo(() => getUniqueCategories(lang), [lang])

  const filtered = useMemo(() => {
    return blogsData.filter((post) => {
      const matchesSearch = post.title[lang].toLowerCase().includes(search.toLowerCase())
      const matchesCategory = category === 'all' || post.category[lang] === category
      return matchesSearch && matchesCategory
    })
  }, [search, category, lang])

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value)
    setPage(1)
  }

  const handleReset = () => {
    setSearch('')
    setCategory('all')
    setPage(1)
  }

  const filterBar = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: '2fr 1fr',
        },
        gap: 2,
        border: '1px solid rgba(217,214,207,0.2)',
        borderRadius: '20px',
        bgcolor: 'rgba(43,43,43,0.85)',
        backdropFilter: 'blur(16px) saturate(160%)',
        WebkitBackdropFilter: 'blur(16px) saturate(160%)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
        p: { xs: 2.5, md: 3.5 },
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={isArabic ? 'بحث عن مقال...' : 'Search articles...'}
        value={search}
        onChange={handleFilterChange(setSearch)}
        sx={fieldSx}
      />

      <TextField
        select
        fullWidth
        size="small"
        label={isArabic ? 'الفئة' : 'Category'}
        value={category}
        onChange={handleFilterChange(setCategory)}
        sx={fieldSx}
      >
        <MenuItem value="all">{isArabic ? 'كل الفئات' : 'All Categories'}</MenuItem>
        {categories.map((cat) => (
          <MenuItem key={cat} value={cat}>{cat}</MenuItem>
        ))}
      </TextField>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ gridColumn: '1 / -1', mt: 1 }}
      >
        <Typography sx={{ color: 'rgba(217,214,207,0.75)', fontSize: '0.85rem' }}>
          {filtered.length} {isArabic ? 'نتيجة' : 'results found'}
        </Typography>
        <Button
          onClick={handleReset}
          startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
          size="small"
          sx={{ color: 'rgba(217,214,207,0.85)', '&:hover': { color: '#7A2E3A' } }}
        >
          {isArabic ? 'إعادة تعيين' : 'Reset'}
        </Button>
      </Stack>
    </Box>
  )

  return (
    <Box sx={{ bgcolor: '#2B2B2B', minHeight: '100vh' }}>
      <Navebarehero />
      <BlogsPageHero isArabic={isArabic} filterBar={filterBar} />

      <Box
        sx={{
          maxWidth: 1300,
          mx: 'auto',
          px: { xs: 3, md: 6 },
          pt: { xs: 16, md: 14 },
          pb: { xs: 10, md: 14 },
          minHeight: '60vh',
        }}
      >
        {/* الشبكة - 3 كروت في الصف بالظبط */}
        {paginated.length > 0 ? (
          <AnimatePresence mode="wait">
            <Box
              key={page + search + category}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 3,
              }}
            >
              {paginated.map((post, index) => (
                <Box
                  key={post.id}
                  component={motion.article}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  onClick={() => navigate(`/blogs/${post.slug}`)}
                  sx={{
                    position: 'relative',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    border: '1px solid rgba(217,214,207,0.12)',
                    bgcolor: 'rgba(217,214,207,0.02)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'border-color 0.3s ease, transform 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(122,46,58,0.5)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  {/* الصورة */}
                  <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', overflow: 'hidden', flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={post.image}
                      alt={post.title[lang]}
                      loading="lazy"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <Chip
                      label={post.category[lang]}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        [isArabic ? 'right' : 'left']: 12,
                        bgcolor: 'rgba(122,46,58,0.85)',
                        color: '#D9D6CF',
                        fontSize: '0.75rem',
                        zIndex: 3,
                      }}
                    />
                  </Box>

                  {/* ✨ gradient على مستوى الكارت كله - يبدأ من فوق منطقة التفاصيل ويمتد جوه الصورة */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(180deg, rgba(43,43,43,0) 0%, rgba(43,43,43,0) 55%, rgba(43,43,43,0.4) 72%, rgba(43,43,43,0.85) 88%, rgba(217,214,207,0.02) 100%)',
                      pointerEvents: 'none',
                      zIndex: 2,
                    }}
                  />

                  {/* التفاصيل */}
                  <Stack spacing={1} sx={{ p: 3, flexGrow: 1, position: 'relative', zIndex: 3 }}>
                    <Typography
                      sx={{
                        fontFamily: headingFont,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        color: '#D9D6CF',
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {post.title[lang]}
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: bodyFont,
                        color: 'rgba(217,214,207,0.65)',
                        fontSize: '0.85rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {post.excerpt[lang]}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    <Stack direction="row" alignItems="center" spacing={0.6} sx={{ pt: 0.5 }}>
                      <CalendarTodayIcon sx={{ fontSize: 14, color: '#D9A5A5' }} />
                      <Typography sx={{ fontFamily: bodyFont, color: 'rgba(217,214,207,0.6)', fontSize: '0.78rem' }}>
                        {post.dateDisplay[lang]}
                      </Typography>
                    </Stack>

                    <Button
                      fullWidth
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 16, transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
                      sx={{
                        justifyContent: 'space-between',
                        mt: 1.5,
                        py: 1,
                        borderRadius: '999px',
                        border: '1px solid rgba(217,214,207,0.25)',
                        color: '#D9D6CF',
                        '&:hover': { borderColor: '#7A2E3A', bgcolor: 'rgba(122,46,58,0.08)' },
                      }}
                    >
                      {isArabic ? 'اقرأ المزيد' : 'Read More'}
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Box>
          </AnimatePresence>
        ) : (
          <Typography sx={{ color: 'rgba(217,214,207,0.5)', textAlign: 'center', py: 10 }}>
            {isArabic ? 'لا توجد نتائج' : 'No results found'}
          </Typography>
        )}

        {/* الباجينيشن */}
        {totalPages > 1 && (
          <Stack alignItems="center" sx={{ mt: 6 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              dir={isArabic ? 'rtl' : 'ltr'}
              sx={{
                '& .MuiPaginationItem-root': { color: 'rgba(217,214,207,0.7)', borderColor: 'rgba(217,214,207,0.2)' },
                '& .Mui-selected': { bgcolor: '#7A2E3A !important', color: '#D9D6CF' },
              }}
            />
          </Stack>
        )}
      </Box>
    </Box>
  )
}